import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, { useEffect, useRef } from 'react';
import Div100vh, { use100vh } from 'react-div-100vh';
import styled from 'styled-components';
import { customScrollBar } from '../utils/styledComponentsUtils';

interface Props {
  showModal: boolean;
  onClose?: () => void;
}

const BasicModal: React.FC<React.PropsWithChildren<Props>> = ({
  showModal,
  onClose = () => null,
  children,
  ...otherProps
}) => {
  const divHeight = use100vh();
  const height = divHeight ? `${divHeight * 0.9}px` : '90vh';
  const drawerHandle = document.querySelector('.drawer__handle-wrapper');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showModal && ref.current) {
      /**
       * IOS SAFARI BUG FIX
       * This disables the bottom drawer handle to prevent grabbing the handle while this modal is open.
       */
      if (drawerHandle)
        (drawerHandle as HTMLElement).style.pointerEvents = 'none';
      disableBodyScroll(ref.current);
    } else {
      if (drawerHandle)
        (drawerHandle as HTMLElement).style.pointerEvents = 'auto';
      clearAllBodyScrollLocks();
    }
  }, [showModal]);

  if (showModal)
    return (
      <>
        <Wrapper {...otherProps}>
          <BlackBackground onClick={onClose} />
          <ContentBox ref={ref} height={height}>
            {children}
          </ContentBox>
        </Wrapper>
      </>
    );

  return null;
};

const ContentBox = styled.div<{ height?: string }>`
  max-height: ${({ height }) => height};
  overflow: auto;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(40, 41, 61, 0.08),
    0 20px 32px rgba(96, 97, 112, 0.24);
  border-radius: 6px;
  position: relative;
  animation: offerPopup 0.2s;
  margin: 0 0.8rem;

  @keyframes offerPopup {
    from {
      transform: translateY(100vh);
    }
    to {
      transform: translateY(0);
    }
  }

  ${customScrollBar}
`;

const Wrapper = styled(Div100vh)`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 100%;
  z-index: 11;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BlackBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  animation: opacityFade 0.2s;
  @keyframes opacityFade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export default BasicModal;
